import { CustomerSubscriptionModel, OrderLineStatusEnum, SubscriptionTypeEnum } from "@rightship-tech/common-ui/models/rightstore/rightstore.models";
import { RightStoreService } from "@rightship-tech/common-ui/services/rightstore/rightstore.service";
import { ProductCodes } from "@rightship-tech/tasks-ui/constants/product.constant";
import { TaskModel, TaskPriorityEnum } from "@rightship-tech/tasks-ui/models/tasks.models";
import { MyVesselsRouteConstants } from "../constants/route.constants";
import { Router } from "aurelia-router";
import { UserClientService } from "@rightship-tech/common-ui/services/basic/user-client.service";
import { CustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { myVesselsDialogConstants } from "../constants/dialog.constants";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { TaskService } from "@rightship-tech/tasks-ui/services/task.service";
import { autoinject } from "aurelia-framework";
import { Claims } from "../constants/claims.constants";
import { UserClaimsService } from "@rightship-tech/common-ui/services/basic/user-claims.service";
import { BudgetClientService } from "./rightstore/budget-client.service";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";

@autoinject
export class TaskActionService {

    constructor(private rightStoreService: RightStoreService, private router: Router, private userClientService: UserClientService,
        private customerService: CustomerService, private dialogClientService: DialogClientService, private budgetClientService:BudgetClientService,
        private configService: ConfigService, private taskService: TaskService, private userClaimsService:UserClaimsService) { }

    async hasVesselsInLimitedSubscription(task: TaskModel, subscription: CustomerSubscriptionModel): Promise<boolean> {
        let vesselsInSubscription = await this.rightStoreService.getCustomerSubscriptionVessels(subscription?.customerSubscriptionId);
        return vesselsInSubscription.vessels.find(x => x.vesselId == task.vesselId) != null;
    }

    navigateToSubmitCloseOutInformation(task: TaskModel, orderGuid: string, priority:boolean) {
        this.router.navigateToRoute(MyVesselsRouteConstants.SubmitCloseOutInformation, {
            entityId: task.taskEntityId,
            entityTypeId: task.taskType,
            vesselName: task.vesselName,
            description: task.description,
            date: task.date,
            orderGuid: orderGuid,
            isPriority: priority
        });
    }

    async submitCloseOut(task: TaskModel) {
        let customerId = await this.customerService.getCustomerId();
        let customerSubscriptionResponse = await this.rightStoreService.getCustomerSubscription(customerId, ProductCodes.InformationServiceSubscription);
        let subscription = customerSubscriptionResponse.customerSubscription;
        let isPriority = task.priority == TaskPriorityEnum.Priority;
        let userName = this.userClientService.currentUser.user.email;
        let businessUnitId = await this.customerService.getBusinessUnitId();
        var orderGuid = await this.getOrderGuid(task);
        if (orderGuid) {
            this.navigateToSubmitCloseOutInformation(task, orderGuid, isPriority);
        } else if (await this.hasFullSubscription(subscription) || task.hasVet || await this.hasVesselsInLimitedSubscription(task, subscription)) {
            let urlResp = await this.sendRequestToCreateOrder(task, businessUnitId.toString(), userName, this.getProductNumber(task.taskType));
            await this.taskService.updateTaskGuid(task.taskEntityId, task.taskType, this.extractOrderGuidFromUrl(urlResp), isPriority);
            this.navigateToSubmitCloseOutInformation(task, orderGuid, isPriority);
        } else {
            let queryParams = this.configService.config.environment.toLowerCase() == "production" ? "" : `env=${this.configService.config.environment}&`;
            var productNumber = this.getProductNumber(task.taskType, true);
            var standardDescription = productNumber == ProductCodes.PscCloseOutPriority ? "Standard PSC close out" : "Standard Incident close out";
            var canOverridePriceInAppPurchase = this.userClaimsService.hasPermission(Claims.CanOverridePriceInAppPurchase);

            this.dialogClientService.show(myVesselsDialogConstants.orderSummaryDialog, {
                customerNumber: businessUnitId,
                productNumber: productNumber,
                standardDescription: standardDescription,
                canOverridePriceInAppPurchase: canOverridePriceInAppPurchase,
                creditBalance: await this.budgetClientService.getCreditBalance(businessUnitId),
                userName: this.userClaimsService.hasPermission(Claims.RightShipUser)? "":this.userClientService.currentUser?.user.email,
                queryParams: `${queryParams}EntityId=${task.taskEntityId}&EntityTypeId=${task.taskType}&Priority=1`,
                canCustomerInvoice: true,
                preventRedirect: true,
                afterPurchase: async (url, priority) => {
                    var guid: string = null;
                    if (url) {
                        guid = this.extractOrderGuidFromUrl(url);
                        await this.taskService.updateTaskGuid(task.taskEntityId, task.taskType, guid, priority);
                    }
                    this.navigateToSubmitCloseOutInformation(task, guid, priority);
                },
                processStandardProduct: async () => {
                    return await this.sendRequestToCreateOrder(task, businessUnitId.toString(), userName, this.getProductNumber(task.taskType));
                }
            }, true, false, false, "right");
        }
    }

    async getOrderGuid(task: TaskModel) {
        if (task.orderGuid) return task.orderGuid;

        var resp = await this.taskService.getTask(task.taskEntityId, task.taskType);
        task.orderGuid = resp.orderGuid;
        return task.orderGuid;
    }

    hasFullSubscription(subscription: CustomerSubscriptionModel): boolean {
        return subscription?.subscriptionTypeId == SubscriptionTypeEnum.FullSubscription;
    }

    getProductNumber(entityType: EntityTypes, isPriority: boolean = false): string {
        switch (entityType) {
            case EntityTypes.PSC:
                if (isPriority) {
                    return ProductCodes.PscCloseOutPriority;
                } else {
                    return ProductCodes.PscCloseOutFree;
                }
            case EntityTypes.Incident:
                if (isPriority) {
                    return ProductCodes.IncidentCloseOutPriority;
                } else {
                    return ProductCodes.IncidentCloseOutFree;
                }
        }
    }

    async sendRequestToCreateOrder(task: TaskModel, businessUnitId: string, userName: string, productNumber: string): Promise<string> {
        let orderGuid = await this.getOrderGuid(task);
        var url = "";
        if (!orderGuid) {
            let createCloseOutOrder = {
                customerNumber: businessUnitId,
                userName: userName,
                paymentMethod: 1,
                orderLines: [
                    {
                        productNumber: productNumber,
                        status: OrderLineStatusEnum.Initial,
                        queryParams: `EntityId=${task.taskEntityId}&EntityTypeId=${task.taskType}`
                    }
                ],

            };
            var response = await this.rightStoreService.createOrUpdateOrder(createCloseOutOrder);
            url = response.orderLines[0].url;
        }

        return url;
    }

    navigateToReplyToRfi(task: TaskModel) {
        this.router.navigateToRoute(MyVesselsRouteConstants.RfiCorrespondence, {
            subtaskId: task.subtaskId,
            taskId: task.taskEntityId,
            vesselName: task.vesselName,
            vesselImo: task.vesselImo,
            entityType: task.taskType
        });
    }

    async hasCloseOutSubmited(task: TaskModel): Promise<boolean> {
        let taskCorrespondences = await this.taskService.getTaskCorrespondences(task.taskEntityId, task.taskType);
        let customerCreatedCorrespondences = taskCorrespondences.filter(x => x.isCustomerRFI);
        return customerCreatedCorrespondences.length != 0;
    }

    extractOrderGuidFromUrl(url: string): string {
        var param = url.split('?')[0].split('/');
        return param[param.length - 1];
    }
}